





















import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import { Picker } from "emoji-mart";
import useBreakPoints from "@/hooks/useBreakPoints";
import useDarkModeController from "@/hooks/useDarkModeController";
import appStorage from "@/lib/appStorage";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EmojiPickerProps, EmojiPickerShowProps } from "@/types";
import { EVENTS } from "@/const";
import BaseIconButton from "@/components/BaseIconButton";

const STORAGE_KEY = "emoji-mart.skin";

const skinTones = [
  { color: "#FED35F", value: 1 },
  { color: "#5E493D", value: 6 },
  { color: "#A06940", value: 5 },
  { color: "#C79B75", value: 4 },
  { color: "#C79B75", value: 3 },
  { color: "#FDE5CB", value: 2 },
];

export default defineComponent({
  name: "EmojiPicker",
  components: { BaseIconButton },
  setup() {
    const { isDarkMode } = useDarkModeController();
    const { portraitUp } = useBreakPoints();
    const pickerWrapper = ref<HTMLDivElement>();
    const picker = ref<Element>();
    const container = ref<HTMLElement>();
    const show = ref(false);
    const skinTone = ref(appStorage.getNumber(STORAGE_KEY, 1));
    const customStyle = ref({});

    let unwatch: (() => void) | null;

    const showData = ref<Omit<EmojiPickerShowProps, "show"> | null>(null);

    const onRemove = () => {
      showData.value?.onRemove();
    };

    const calculatePosition = () => {
      customStyle.value = {};

      if (!portraitUp.value || !showData.value) {
        return;
      }
      const offset = 16;
      const pickerHeight = 408;
      const pickerWidth = 448;
      const anchorRect = showData.value.anchorElement.getBoundingClientRect();
      const bottom = window.innerHeight - anchorRect.top + offset;
      const top = window.innerHeight - bottom - pickerHeight + offset;
      const minSpaceHeight = 300 + offset * 2;
      if (anchorRect.top < minSpaceHeight && window.innerHeight - anchorRect.bottom > minSpaceHeight) {
        customStyle.value = {
          top: anchorRect.bottom + offset + "px",
          bottom: offset + "px",
          left: anchorRect.right - pickerWidth - offset + "px",
        };
      } else {
        customStyle.value = {
          top: (top > offset ? top : offset) + "px",
          bottom: bottom + "px",
          left: anchorRect.right - pickerWidth - offset + "px",
        };
      }
    };

    const onEmojiSelect = (data: { native: string }) => {
      showData.value?.onInput(data.native);
    };

    const mountPicker = () => {
      const instance = new Picker({
        previewPosition: "none",
        dynamicWidth: true,
        skinTonePosition: "none",
        theme: isDarkMode.value ? "dark" : "light",
        skin: skinTone.value,
        onEmojiSelect,
      });

      if (instance instanceof Element) picker.value = instance;
      if (pickerWrapper.value && instance instanceof Node) pickerWrapper.value.appendChild(instance);
      picker.value?.setAttribute("placeholder", $t("Search emojis"));
      picker.value?.setAttribute("title", $t("Search emojis"));
    };

    const outsideClickListener = (e: Event) => {
      if (e.target instanceof Node && !container.value?.contains(e.target)) {
        show.value = false;
      }
    };

    const showPicker = () => {
      unwatch = watch(skinTone, skinTone => {
        picker.value?.setAttribute("skin", String(skinTone));
        appStorage.setNumber(STORAGE_KEY, skinTone);
      });
      setTimeout(() => window.addEventListener("click", outsideClickListener), 100);
      calculatePosition();
      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition);
    };

    const close = () => {
      unwatch && unwatch();
      showData.value?.onClose();
      showData.value = null;
      window.removeEventListener("click", outsideClickListener);
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition);
    };

    const onShow = (data?: EmojiPickerProps) => {
      if (!data) return;

      show.value = data.show;
      if (data.show) {
        const { show, ...newShowData } = data;
        showData.value = newShowData;
      }
    };

    watch(show, show => (show ? showPicker() : close()));
    watch(portraitUp, portraitUp => !portraitUp && show.value && (show.value = false));
    watch(isDarkMode, isDarkMode => {
      picker.value?.setAttribute("theme", isDarkMode ? "dark" : "light");
    });

    onMounted(mountPicker);

    emitter.on<EmojiPickerProps>(EVENTS.EMOJI_PICKER_SHOW, onShow);
    onBeforeUnmount(() => {
      emitter.off<EmojiPickerProps>(EVENTS.EMOJI_PICKER_SHOW, onShow);
      close();
    });

    return {
      show,
      pickerWrapper,
      container,
      portraitUp,
      skinTones,
      skinTone,
      customStyle,
      onRemove,
    };
  },
});
